import { Link, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeft, Sun, Moon } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useSidebar } from '../../contexts/SidebarContext';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../hooks/useTheme';
import { NAV_ITEMS, NAV_GROUPS } from '../../constants/navigation';
import { ADMIN_NAV } from '../../constants/adminNav';
import { SidebarGroupIcon } from './SidebarGroupIcon';
import { SidebarPanel } from './SidebarPanel';
import type { UserRole } from '../../types/api';

const COLLAPSED_WIDTH = 'w-16';   // 64px
const EXPANDED_WIDTH = 'w-56';    // 224px

/**
 * AppSidebarV2 — v2 sidebar (preserved for FBR builds)
 * v3 uses LeftNav instead.
 */
export function AppSidebarV2() {
  const { collapsed, activeGroup, isAdminMode, toggle, setActiveGroup } = useSidebar();
  const { user } = useAuth();
  const { isDark, toggle: toggleTheme } = useTheme();
  const location = useLocation();
  const userRole = (user?.role || 'underwriter') as UserRole;

  const varItem = NAV_ITEMS.find(i => i.key === 'var')!;
  const VarIcon = varItem.icon; // Database icon (was TrendingUp)

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const hasActiveChildInGroup = (groupKey: string) => {
    const group = NAV_GROUPS.find(g => g.key === groupKey);
    if (!group) return false;
    return group.items.some(item => isActive(item.path));
  };

  // Shared brand header
  const brandHeader = (
    <div className={cn(
      "flex items-center h-16 border-b border-gray-200 dark:border-slate-800 shrink-0",
      collapsed ? "justify-center px-0" : "px-3",
    )}>
      <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-brand">
        <svg viewBox="0 0 24 24" className="h-6 w-6 shrink-0" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="sidebar-flame" x1="12" y1="2" x2="12" y2="22" gradientUnits="userSpaceOnUse">
              <stop stopColor="#f97316"/>
              <stop offset="1" stopColor="#f59e0b"/>
            </linearGradient>
          </defs>
          <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" fill="url(#sidebar-flame)" stroke="url(#sidebar-flame)" strokeWidth="0.5"/>
        </svg>
        {!collapsed && (
          <span className="text-sm font-bold text-gray-900 dark:text-slate-100 tracking-tight whitespace-nowrap">
            FIREBREAK <span className="text-gray-500 dark:text-slate-400 font-light">BACCHUS</span>
          </span>
        )}
      </Link>
    </div>
  );

  // Shared toggle footer — theme toggle + nav tray close
  const toggleFooter = (
    <div className="border-t border-gray-200 dark:border-slate-800 p-2 shrink-0">
      {collapsed ? (
        /* Collapsed: theme toggle stacked above tray toggle, both centered */
        <div className="flex flex-col items-center gap-1">
          <button
            type="button"
            onClick={toggleTheme}
            data-testid="theme-toggle-desktop"
            className="flex items-center justify-center w-10 h-9 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={toggle}
            data-testid="sidebar-toggle"
            className="flex items-center justify-center w-10 h-9 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors"
            aria-label="Expand sidebar"
          >
            <PanelLeft className="h-5 w-5" />
          </button>
        </div>
      ) : (
        /* Expanded: theme toggle left, tray close right-aligned */
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={toggleTheme}
            data-testid="theme-toggle-desktop"
            className="flex items-center justify-center h-9 px-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors"
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            type="button"
            onClick={toggle}
            data-testid="sidebar-toggle"
            className="flex items-center justify-center h-9 px-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-slate-300 dark:hover:bg-slate-800 transition-colors"
            aria-label="Collapse sidebar"
          >
            <PanelLeftClose className="h-5 w-5" />
          </button>
        </div>
      )}
    </div>
  );

  // Admin mode: show admin nav items
  if (isAdminMode) {
    return (
      <aside
        data-testid="app-sidebar"
        className={cn(
          'hidden lg:flex flex-col bg-white border-r border-gray-200 dark:bg-slate-900 dark:border-slate-800 h-screen sticky top-0 transition-[width] duration-200',
          collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        )}
      >
        {brandHeader}
        <nav className="flex-1 overflow-y-auto py-2">
          {collapsed ? (
            <div className="flex flex-col items-center gap-1 px-2">
              {ADMIN_NAV.map(item => (
                <Link
                  key={item.key}
                  to={item.path}
                  title={item.label}
                  data-testid={`sidebar-item-${item.key}`}
                  className={cn(
                    'flex items-center justify-center w-10 h-10 rounded-md transition-colors',
                    isActive(item.path)
                      ? 'bg-gray-100 text-gray-900 dark:bg-slate-800 dark:text-slate-100'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
                  )}
                >
                  <item.icon className="h-5 w-5" />
                </Link>
              ))}
            </div>
          ) : (
            <SidebarPanel title="Admin Console" items={ADMIN_NAV} />
          )}
        </nav>
        {toggleFooter}
      </aside>
    );
  }

  // Normal mode: tiered navigation
  return (
    <aside
      data-testid="app-sidebar"
      className={cn(
        'hidden lg:flex flex-col bg-white border-r border-gray-200 dark:bg-slate-900 dark:border-slate-800 h-screen sticky top-0 transition-[width] duration-200',
        collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
      )}
    >
      {brandHeader}
      <nav className="flex-1 overflow-y-auto py-2">
        {collapsed ? (
          /* Collapsed: icon rail */
          <div className="flex flex-col items-center gap-1 px-2">
            {/* Data Lake standalone */}
            <Link
              to={varItem.path}
              title={varItem.label}
              data-testid="sidebar-item-var"
              className={cn(
                'flex items-center justify-center w-10 h-10 rounded-md transition-colors',
                isActive(varItem.path)
                  ? 'bg-gray-100 text-gray-900 dark:bg-slate-800 dark:text-slate-100'
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
              )}
            >
              <VarIcon className="h-5 w-5" />
            </Link>

            <div className="w-6 border-t border-gray-200 dark:border-slate-800 my-1" />

            {/* Group icons */}
            {NAV_GROUPS.map(group => {
              const visible = group.items.filter(i => !i.roles || i.roles.includes(userRole));
              if (visible.length === 0) return null;
              return (
                <SidebarGroupIcon
                  key={group.key}
                  groupKey={group.key}
                  icon={group.icon}
                  label={group.label}
                  isActive={activeGroup === group.key}
                  hasActiveChild={hasActiveChildInGroup(group.key)}
                  collapsed={collapsed}
                  onClick={() => {
                    const isSelecting = activeGroup !== group.key;
                    setActiveGroup(isSelecting ? group.key : null);
                    if (isSelecting) toggle();
                  }}
                />
              );
            })}
          </div>
        ) : (
          /* Expanded: full nav */
          <div className="px-2">
            {/* Data Lake standalone */}
            <div className="mb-2">
              <Link
                to={varItem.path}
                data-testid="sidebar-item-var"
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors',
                  isActive(varItem.path)
                    ? 'bg-gray-100 text-gray-900 dark:bg-slate-800 dark:text-slate-100'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
                )}
              >
                <VarIcon className="h-5 w-5 shrink-0" />
                <span className="truncate">{varItem.label}</span>
              </Link>
              {varItem.children?.map(child => (
                <Link
                  key={child.key}
                  to={child.path}
                  data-testid={`sidebar-item-${child.key}`}
                  className={cn(
                    'flex items-center gap-3 pl-11 pr-3 py-1.5 text-sm transition-colors',
                    isActive(child.path)
                      ? 'text-gray-900 font-medium dark:text-slate-100'
                      : 'text-gray-500 hover:text-gray-900 dark:text-slate-500 dark:hover:text-slate-100',
                  )}
                >
                  {child.label}
                </Link>
              ))}
            </div>

            <div className="border-t border-gray-200 dark:border-slate-800 my-2" />

            {/* Groups */}
            {NAV_GROUPS.map(group => {
              const visible = group.items.filter(i => !i.roles || i.roles.includes(userRole));
              if (visible.length === 0) return null;
              const isGroupActive = activeGroup === group.key;
              const hasActive = hasActiveChildInGroup(group.key);

              return (
                <div key={group.key} className="mb-1">
                  <button
                    type="button"
                    onClick={() => setActiveGroup(isGroupActive ? null : group.key)}
                    data-testid={`sidebar-group-${group.key}`}
                    aria-expanded={isGroupActive || hasActive}
                    className={cn(
                      'flex items-center gap-3 w-full px-3 py-2 rounded-md text-sm font-medium transition-colors',
                      isGroupActive || hasActive
                        ? 'text-gray-900 bg-gray-50 dark:text-slate-100 dark:bg-slate-800'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
                    )}
                  >
                    <group.icon className="h-5 w-5 shrink-0" />
                    <span className="flex-1 text-left truncate">{group.label}</span>
                  </button>

                  {/* Items — show if group is active or has active child */}
                  {(isGroupActive || hasActive) && (
                    <div className="mt-0.5 ml-4 border-l border-gray-200 dark:border-slate-800 pl-3">
                      {visible.map(item => (
                        <Link
                          key={item.key}
                          to={item.path}
                          data-testid={`sidebar-item-${item.key}`}
                          className={cn(
                            'flex items-center gap-3 px-3 py-1.5 text-sm rounded-md transition-colors',
                            isActive(item.path)
                              ? 'text-gray-900 font-medium bg-gray-50 dark:text-slate-100 dark:bg-slate-800'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-800',
                          )}
                        >
                          <item.icon className="h-4 w-4 shrink-0" />
                          <span className="flex-1 truncate">{item.label}</span>
                          {item.badge === 'preview' && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300">
                              Preview
                            </span>
                          )}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </nav>
      {toggleFooter}
    </aside>
  );
}
